import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, X } from "lucide-react";
import { Button } from "./ui";

type DialogProps = {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
  closeDisabled?: boolean;
};

const widths = {
  sm: "max-w-md",
  md: "max-w-2xl",
  lg: "max-w-4xl",
  xl: "max-w-6xl",
};

export function Dialog({ open, onClose, title, description, children, size = "md", closeDisabled = false }: DialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    document.body.style.overflow = "hidden";
    const focusableSelector = 'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';
    const focusableElements = () => Array.from(panelRef.current?.querySelectorAll<HTMLElement>(focusableSelector) ?? []).filter(element => !element.hasAttribute("hidden"));
    (focusableElements()[0] ?? panelRef.current)?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      const dialogRoots = document.querySelectorAll<HTMLElement>("[data-dialog-root]");
      if (dialogRoots[dialogRoots.length - 1] !== rootRef.current) return;
      if (event.key === "Escape" && !closeDisabled) onCloseRef.current();
      if (event.key !== "Tab") return;
      const elements = focusableElements();
      if (elements.length === 0) {
        event.preventDefault();
        panelRef.current?.focus();
        return;
      }
      const first = elements[0];
      const last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
      previousFocus?.focus();
    };
  }, [closeDisabled, open]);

  if (!open) return null;
  return createPortal(
    <div
      ref={rootRef}
      data-dialog-root
      className="fixed inset-0 z-[2000] flex items-end justify-center bg-navy/60 p-0 backdrop-blur-sm md:items-center md:p-4"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !closeDisabled) onClose();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
        className={`max-h-[calc(100dvh-1rem)] w-full overflow-y-auto rounded-t-[1.75rem] bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl outline-none md:max-h-[calc(100vh-2rem)] md:rounded-3xl ${widths[size]}`}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-5 border-b bg-white/95 px-5 py-4 backdrop-blur md:px-6 md:py-5">
          <div>
            <h2 id={titleId} className="text-xl font-bold text-navy">{title}</h2>
            {description && <p id={descriptionId} className="mt-1 text-sm leading-6 text-slate-500">{description}</p>}
          </div>
          <button
            type="button"
            aria-label="Pencereyi kapat"
            disabled={closeDisabled}
            onClick={onClose}
            className="rounded-xl p-2 text-slate-400 transition hover:bg-slate-100 hover:text-navy disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-5 md:p-6">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

type ConfirmDialogProps = {
  open: boolean;
  title: string;
  description: string;
  confirmLabel?: string;
  busy?: boolean;
  tone?: "default" | "danger";
  onConfirm: () => void | Promise<void>;
  onClose: () => void;
};

export function ConfirmDialog({ open, title, description, confirmLabel = "Onayla", busy = false, tone = "default", onConfirm, onClose }: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} title={title} size="sm" closeDisabled={busy}>
      <div className="flex gap-4">
        <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-2xl ${tone === "danger" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-600"}`}>
          <AlertTriangle size={21} />
        </span>
        <p className="text-sm leading-6 text-slate-600">{description}</p>
      </div>
      <div className="mt-7 flex justify-end gap-3">
        <Button type="button" variant="ghost" disabled={busy} onClick={onClose}>Vazgeç</Button>
        <Button type="button" loading={busy} onClick={() => void onConfirm()}>{confirmLabel}</Button>
      </div>
    </Dialog>
  );
}
